import { nodeToJSON } from '@common/prosemirror/json/prosemirror-json';
import { ProseMirrorNodeJSON } from '@common/prosemirror/model/json';
import { CustomError } from '@common/util/custom-error';
import diff from 'fast-diff';
import { AddOperation, applyPatch as fastJSONPatchApplyPatch, compare as fastJSONPatchCreatePatch, Operation, RemoveOperation, ReplaceOperation } from 'fast-json-patch';
import { isEqual } from 'lodash';
import { ProseMirrorNode, ReplaceError, Schema } from 'prosemirror-model';
import { ReplaceStep, Transform } from 'prosemirror-transform';
import { applyPatch as rfc6902ApplyPatch, createPatch as rfc6902CreatePatch } from 'rfc6902';

/*
 * The code in this file was originally based on https://gitlab.com/mpapp-public/prosemirror-recreate-steps
 * Much of the code has been rewritten to be faster and handle larger documents.
 * There are still some functions from the original code that are used in this file.
 */

/**
 * The interface for the JSON patcher used to apply patches to ProseMirror documents.
 * This allows us to use different JSON patch libraries interchangeably.
 */
interface JSONPatcher {
  applyPatch: <T>(document: T, patch: ReadonlyArray<Operation>) => void;
  createPatch: (tree1: Object | Array<any>, tree2: Object | Array<any>) => Operation[];
}

export type JSONPatcherType = 'rfc6902' | 'fast-json-patch';

/**
 * The JSON patcher that uses the RFC6902 library to apply patches to ProseMirror documents.
 * This is the default patcher used in the ProseMirror recreation steps.
 */
const rfc6902JSONPatcher: JSONPatcher = {
  applyPatch: rfc6902ApplyPatch,
  createPatch: rfc6902CreatePatch
};

/**
 * The JSON patcher that uses the fast-json-patch library to apply patches to ProseMirror documents.
 * This is used as a fallback when the RFC6902 patcher fails due to a stack size limit error.
 */
const fastJSONPatchJSONPatcher: JSONPatcher = {
  applyPatch: fastJSONPatchApplyPatch,
  createPatch: fastJSONPatchCreatePatch
};

/** The runtime error message when the call stack size is exceeded. Used to detect this error. */
const StackSizeLimitError = 'Maximum call stack size exceeded';

/**
 * Checks if a string is a number.
 * @param value The value to check.
 * @returns True if the value is a number, false otherwise.
 */
function isNumber(value: string): boolean {
  return !isNaN(parseInt(value, 10));
}

/**
 * Gets the replace step between two documents.
 * @param fromDoc The document to replace from.
 * @param toDoc The document to replace to.
 * @returns The replace step or false if no step can be made.
 */
function getReplaceStep(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode): ReplaceStep | false {
  let start = toDoc.content.findDiffStart(fromDoc.content);
  if (start === null) {
    return false;
  }

  let {
    a: endA,
    b: endB
  } = toDoc.content.findDiffEnd(fromDoc.content);

  const overlap = start - Math.min(endA, endB);

  if (overlap > 0) {
    // If there is an overlap, there is some freedom of choice in how to calculate the start/end boundary.
    // for an inserted/removed slice. We choose the extreme with the lowest depth value.
    if (fromDoc.resolve(start - overlap).depth < toDoc.resolve(endA + overlap).depth) {
      start -= overlap
    } else {
      endA += overlap
      endB += overlap
    }
  }

  return new ReplaceStep(start, endB, toDoc.slice(start, endA));
}

/**
 * Simplifies the transform by merging adjacent steps that can be merged.
 * @param transform The transform to simplify.
 * @returns The simplified transform.
 */
function simplifyTransform(transform: Transform): Transform {
  if (!transform.steps.length) {
    return transform;
  }

  const newTr = new Transform(transform.docs[0]);
  const oldSteps = transform.steps.slice();

  while (oldSteps.length > 0) {
    let step = oldSteps.shift();

    while (oldSteps.length > 0 && step.merge(oldSteps[0])) {
      const addedStep = oldSteps.shift();
      if (step instanceof ReplaceStep && addedStep instanceof ReplaceStep) {
        step = getReplaceStep(newTr.doc, addedStep.apply(step.apply(newTr.doc).doc).doc) as ReplaceStep;
        // If no step is returned that means the merged steps do not change the document so they are unnecessary
        if (!step) {
          // Drop the merged steps and move onto the next one
          step = oldSteps.shift();
        }
      } else {
        step = step.merge(addedStep);
      }
    }

    if (step) {
      newTr.step(step);
    }
  }

  return newTr;
}

/**
 * Gets the next operations that act on the same node.
 * Removes the next operations from the ops array.
 * @param ops The operations to get the next operations from.
 * @returns The next operations that act on the same node.
 */
function getNextOperationsOnSameNode(ops: Operation[]): Operation[] {
  const nextOperations: Operation[] = [];
  const nodePath = ops[0]?.path.split('/').slice(0, -1).join('/');

  while (ops.length > 0) {
    const op = ops.shift();
    nextOperations.push(op);

    if (ops.length > 0) {
      const nextPathParts = ops[0].path.split('/');
      if (nextPathParts.slice(0, -1).join('/') === nodePath) {
        continue;
      } else {
        break;
      }
    }
  }

  return nextOperations;
}

/**
 * Creates a transformation to change one document to another.
 * Simply replaces the entire document with the new one.
 * @param fromDoc The document to change from.
 * @param toDoc The document to change to.
 * @returns The transform that can be applied to the document to get the new document.
 */
function applyFullReplace(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode): Transform {
  const transform = new Transform(fromDoc);
  transform.replaceWith(0, fromDoc.content.size, toDoc);
  return transform;
}

/**
 * Finds a node at the given path.
 * @param node The node to search in.
 * @param pathParts The path to search for.
 * @returns The found node, its path index, and its start position in the document.
 */
function findNodeAtPath(node: ProseMirrorNode, pathParts: string[]): { node: ProseMirrorNode, pathIndex: number, start: number } {
  let currentNode = node;
  let pathIndex: number;
  let start = 0;

  pathParts = pathParts.slice(0, -1);

  for (let i = 0; i < pathParts.length; i += 1) {
    const part = pathParts[i];
    if (part === '') {
      continue;
    }

    // If this path is into the children of a node
    if (part === 'content') {
      if (i + 1 < pathParts.length) {
        const index = parseInt(pathParts[i + 1], 10);
        // Increment the start by 1 to account for the current node's start token (do not include the root node as it's tokens are not a part of the document size)
        if (currentNode.type.name !== 'doc') {
          start += 1;
        }

        // Increment the start by the size of all previous children
        for (let j = 0; j < index; j += 1) {
          start += currentNode.child(j).nodeSize;
        }

        currentNode = currentNode.child(index);
        i += 1;
        pathIndex = i;
      }
    } else {
      break;
    }
  }

  return {
    node: currentNode,
    pathIndex,
    start
  };
}

/**
 * Finds the parent node at the given path.
 * @param node The node to search in.
 * @param pathParts The path to search to search for.
 * @param childPathIndex The index of the child in the path parts.
 * @returns The parent node and the index of the child in the parent node.
 */
function findParentNode(node: ProseMirrorNode, childNode: ProseMirrorNode, pathParts: string[], childPathIndex: number): { childIndex: number, parentNode: ProseMirrorNode } {
  const parentPathParts = pathParts.slice(0, childPathIndex);
  const parentNode = findNodeAtPath(node, parentPathParts).node;

  let childIndex: number;
  for (let i = 0; i < parentNode.childCount; i += 1) {
    if (parentNode.child(i).eq(childNode)) {
      childIndex = i;
      break;
    }
  }

  return { childIndex, parentNode };
}

/**
 * Adds steps to the transform by applying the smallest possible changes to a text node.
 * Uses a text diffing algorithm to find the differences.
 * @param transform The transform to apply the steps to.
 * @param op The operation to apply to the text node.
 * @param node The text node to apply the operation to.
 * @param start The start position of the text node in the document.
 * @param schema The schema the node is a part of.
 */
function applyTextReplace(transform: Transform, op: ReplaceOperation<string>, node: ProseMirrorNode, start: number, schema: Schema) {
  const marks = node.marks;
  const fromText = node.text;
  const toText = op.value;
  const textDiffs = diff(fromText, toText);

  for (let i = 0, offset = start; i < textDiffs.length; i++) {
    const [operation, value] = textDiffs[i];
    if (operation === 1) { // inserted
      transform.insert(
        offset,
        schema.nodeFromJSON({ type: 'text', text: value }).mark(marks)
      );
      offset += value.length;
    } else if (operation === -1) { // removed
      transform.delete(offset, offset + value.length);
      // Do not advance offset, as text was removed
    } else { // equal
      offset += value.length;
    }
  }
}

/**
 * Checks if the operations are all node insertions.
 * @param operations The operations to check.
 * @returns True if the operations are all node insertions, false otherwise.
 */
function isNodeInsertions(operations: Operation[]): operations is AddOperation<ProseMirrorNodeJSON>[] {
  return operations.every(op => {
    if (op.op === 'add') {
      const pathParts = op.path.split('/');
      // Check if the path is a content insertion. The index can be a number or a dash for appending.
      return pathParts.at(-2) === 'content' && (isNumber(pathParts.at(-1)) || pathParts.at(-1) === '-');
    }
    return false;
  });
}

/**
 * Adds steps to the transform by applying a node insertion.
 * @param transform The transform to apply the steps to.
 * @param op The operation to apply to the node.
 * @param node The node to apply the operation to. This is the parent node of the node being inserted.
 * @param start The start position of the node in the document. This is the parent node's start position.
 * @param insertAtIndex The child index to insert the node at.
 * @param schema The schema the node is a part of.
 */
function applyNodeInsertion(transform: Transform, op: AddOperation<ProseMirrorNodeJSON>, node: ProseMirrorNode, start: number, insertAtIndex: number, schema: Schema) {
  let offset = 1; // +1 to account for the start token of the parent node

  // Increase the offset by the size of all previous children
  for (let i = 0; i < insertAtIndex; i += 1) {
    offset += node.child(i).nodeSize;
  }

  // Insert the new node at the correct position
  transform.insert(start + offset, schema.nodeFromJSON(op.value));
}

/**
 * Checks if the operations are all node removals.
 * @param operations The operations to check.
 * @returns True if the operations are all node removals, false otherwise.
 */
function isNodeRemovals(operations: Operation[]): operations is RemoveOperation[] {
  return operations.every(op => {
    if (op.op === 'remove') {
      const pathParts = op.path.split('/');
      return pathParts.at(-2) === 'content' && isNumber(pathParts.at(-1));
    }
    return false;
  });
}

/**
 * Adds steps to the transform by applying a node removal.
 * @param transform The transform to apply the steps to.
 * @param op The operation to apply to the node.
 * @param node The node to apply the operation to. This is the parent node of the node being removed.
 * @param start The start position of the node in the document. This is the parent node's start position.
 * @param removeAtIndex The child index to remove the node at.
 * @param schema The schema the node is a part of.
 */
function applyNodeRemoval(transform: Transform, op: RemoveOperation, node: ProseMirrorNode, start: number, removeAtIndex: number, schema: Schema) {
  let offset = 1; // +1 to account for the start token of the parent node

  // Increase the offset by the size of all previous children
  for (let i = 0; i < removeAtIndex; i += 1) {
    offset += node.child(i).nodeSize;
  }

  // Remove the node at the correct position
  transform.delete(start + offset, start + offset + node.child(removeAtIndex).nodeSize);
}

/**
 * Checks if the operations are all node markup changes.
 * @param operations The operations to check.
 * @returns True if the operations are all node markup changes, false otherwise.
 */
function isNodeMarkupChanges(operations: Operation[]): boolean {
  return operations.every(op => {
    const pathParts = op.path.split('/');
    return pathParts.includes('attrs') || pathParts.includes('type');
  });
}

/**
 * Adds steps to the transform by applying the operations to the node.
 * @param transform The transform to apply the steps to.
 * @param operations The operations to apply to the node.
 * @param schema The schema the node is a part of.
 */
function applyOperationsToNode(transform: Transform, operations: Operation[], schema: Schema, jsonPatcher: JSONPatcher) {
  const pathParts = operations[0].path.split('/');
  const { node, pathIndex, start } = findNodeAtPath(transform.doc, pathParts);

  // Grab the node's JSON to apply the operations to
  const nodeJSON: ProseMirrorNodeJSON = nodeToJSON(node);

  // Apply the operations to the node's JSON.
  // The operations are for the full doc so change the paths to be relative to the node.
  jsonPatcher.applyPatch(nodeJSON, operations.map(op => {
    return {
      ...op,
      path: '/' + op.path.split('/').slice(pathIndex + 1).join('/')
    };
  }));

  // Optimization: If this change is only affecting the node's type or attrs, then just set the node markup
  if (nodeJSON.type !== 'text' && isNodeMarkupChanges(operations)) {
    // Make the new node from the new JSON
    const newNode = schema.nodeFromJSON(nodeJSON);

    // Grab the parent node to check if the replacement node is valid
    const { childIndex, parentNode } = findParentNode(transform.doc, node, pathParts, pathIndex);

    // Check if the node can be replaced and that the new node can contain the original node's content
    if (!parentNode.canReplaceWith(childIndex, childIndex + 1, newNode.type, newNode.marks) || !newNode.type.validContent(node.content)) {
      throw new CustomError('Operation Cannot Be Applied', 'Cannot create next step');
    }

    transform.setNodeMarkup(start, newNode.type, newNode.attrs, newNode.marks);
  }
  // Optimization: if this change is only affecting the node's text, then apply text diffing to find the smallest possible change
  else if (operations.length === 1 && operations[0].op === 'replace' && pathParts.at(-1) === 'text') {
    applyTextReplace(transform, operations[0], node, start, schema);
  }
  // Optimization: if this change is only adding a node to the content, then just insert the node at the correct position instead of replacing the parent node
  else if (isNodeInsertions(operations)) {
    operations.forEach(op => {
      const pathParts = op.path.split('/');
      const { node, start } = findNodeAtPath(transform.doc, pathParts);
      // The insertAtIndex can be a number or a dash for appending
      const insertAtIndex = pathParts.at(-1) === '-' ? node.childCount : parseInt(pathParts.at(-1), 10);
      applyNodeInsertion(transform, op, node, start, insertAtIndex, schema);
    });
  }
  // Optimization: if this change is only removing a node from the content, then just remove the node at the correct position instead of replacing the parent node
  else if (isNodeRemovals(operations)) {
    for (let i = 0; i < operations.length; i += 1) {
      const op = operations[i];
      const pathParts = op.path.split('/');
      const { node, start } = findNodeAtPath(transform.doc, pathParts);
      const startingChildCount = node.childCount;
      const removeAtIndex = parseInt(pathParts.at(-1), 10);

      applyNodeRemoval(transform, op, node, start, removeAtIndex, schema);

      // If more than one node was removed by applyNodeRemoval, then stop processing the operations.
      // Removing nodes can cause adjacent nodes to be merged, which can cause the indices from the operations to no longer be valid (the operations only expect one node to be removed at a time.)
      // For example adjacent text nodes will merge if an inline sibling was removed from between them.
      // Breaking now will allow a new set of patch operations to be created that will handle the new document structure.
      if (node.childCount !== startingChildCount - 1) {
        break;
      }
    };
  } else {
    // Make the new node from the new JSON
    const newNode = schema.nodeFromJSON(nodeJSON);

    // Optimization: if only the node's content changed (eg all of a paragraph's text was removed), then just replace its content.
    // Replacing the whole node would also replace its type and attrs, so when the step is rebased onto a doc where the node's attrs
    // changed (eg an incoming commit changed the paragraph's class) it would put the old attrs back.
    if (!node.isLeaf && node.sameMarkup(newNode) && node.type.validContent(newNode.content)) {
      // The root's content starts at 0, any other node's content starts after its start token
      const contentStart = node === transform.doc ? 0 : start + 1;
      transform.replaceWith(contentStart, contentStart + node.content.size, newNode.content);
    } else {
      // Grab the parent node to check if the replacement node is valid
      const { childIndex, parentNode } = findParentNode(transform.doc, node, operations[0].path.split('/'), pathIndex);

      // Check if the node can be replaced
      if (!parentNode.canReplaceWith(childIndex, childIndex + 1, newNode.type, newNode.marks) && node.type !== newNode.type) {
        // TODO: instead of bailing and relying on a full doc replace can we just remove this node entirely right here and then let it get fixed on the next compare?
        throw new CustomError('Operation Cannot Be Applied', 'Cannot create next step');
      }

      // Replace the node
      transform.replaceWith(start, start + node.nodeSize, newNode);
    }
  }
}

/**
 * Resolves a child index path from the root of a document to the node at the end of it.
 * @param doc The document to resolve the path in.
 * @param path The child index at each depth, starting from the root. An empty path resolves to the root.
 * @returns The node at the path and the position before it in the document (-1 for the root, which has no position).
 * @throws RangeError when an index in the path is out of range.
 */
function resolveIndexPath(doc: ProseMirrorNode, path: number[]): { node: ProseMirrorNode, pos: number } {
  let node = doc;
  let pos = -1;

  for (let i = 0; i < path.length; i += 1) {
    pos = getChildPos(node, pos, path[i]);
    node = node.child(path[i]);
  }

  return { node, pos };
}

/**
 * Gets the position before a child of a node, or the end of the node's content when the index is the child count.
 * @param node The parent node.
 * @param pos The position before the parent node (-1 for the root, whose content starts at 0).
 * @param index The index of the child.
 * @returns The position before the child.
 */
function getChildPos(node: ProseMirrorNode, pos: number, index: number): number {
  // +1 to step past the parent's start token (for the root this moves from -1 to the start of its content)
  let childPos = pos + 1;
  for (let i = 0; i < index; i += 1) {
    childPos += node.child(i).nodeSize;
  }

  return childPos;
}

/**
 * Creates a transformation to change the node at a path into another node.
 * Uses a JSON diffing algorithm to find the differences between the two nodes and applies them as steps to the transform.
 * The diff only covers the node at the path so its cost scales with the size of that node rather than the whole document.
 * @param transform The transform to add the steps to.
 * @param path The child index path of the node to change.
 * @param toNode The node to change it to.
 * @param jsonPatcher The JSON patcher to use for applying the patch.
 */
function applyJSONPatch(transform: Transform, path: number[], toNode: ProseMirrorNode, jsonPatcher: JSONPatcher) {
  // 1. Generate json patch operations
  // 2. Grab first set of operations that act on the same node
  // 3. Apply the json patch operations to the JSON version of the node
  // 4. Replace the node in the doc with a new node made from the JSON
  // 5. Repeat until all operations are applied

  const schema = transform.doc.type.schema;
  const toJSON = nodeToJSON(toNode);
  // The operations are applied with paths from the root of the doc, so prefix the paths of the node's diff with the node's path
  const pathPrefix = path.map(index => `/content/${index}`).join('');
  const createPatch = (): Operation[] => {
    const ops = jsonPatcher.createPatch(nodeToJSON(resolveIndexPath(transform.doc, path).node), toJSON);
    return pathPrefix ? ops.map(op => ({ ...op, path: pathPrefix + op.path })) : ops;
  };
  let ops = createPatch();

  // Loop through the operations and apply them to the doc until there are no more operations left
  while (ops.length > 0) {
    const oldOps = [...ops];
    // Grab the set of operations that all act on the same next node
    const nextOperations = getNextOperationsOnSameNode(ops);

    // Apply the operations to the node
    applyOperationsToNode(transform, nextOperations, schema, jsonPatcher);

    // TODO: it may be possible to not have to compare the docs again. There are some failing tests but it would be a huge optimization if we can remove this step.
    // After a node is changed, get new operations for the current node
    const newOps = createPatch();

    // Detect an infinite loop where the operations are the same as the previous ones
    if (isEqual(oldOps, newOps)) {
      throw new CustomError('Operation Cannot Be Applied', 'Unable to change doc with operations')
    }

    ops = newOps;
  }
}

/** A change to one child of a node, found by aligning the children of the node to change from with the children of the node to change to. */
type ChildEdit =
  /** Changes the child at `fromIndex` into the child at `toIndex`. */
  { type: 'patch', fromIndex: number, toIndex: number } |
  /** Deletes the child at `fromIndex`. */
  { type: 'delete', fromIndex: number } |
  /** Inserts the child at `toIndex` before the child at `fromIndex` (or at the end when `fromIndex` is the child count). */
  { type: 'insert', fromIndex: number, toIndex: number };

/**
 * The most edits (children deleted plus children inserted) to look for when aligning two lists of children by a key.
 * Lists that differ by more are aligned by the next, looser key instead, which bounds the time and memory an alignment takes.
 */
const MaxAlignmentEdits = 2000;

/**
 * The keys children are aligned by, from strictest to loosest.
 * Children are paired by the first key, then the runs of children left between those pairs are paired by the next key, and so on.
 * Each key is built from the child and its text content.
 */
const ChildAlignmentKeys: ((node: ProseMirrorNode, text: string) => string)[] = [
  // Children that are probably unchanged. A pair isn't guaranteed to be equal, so it's still checked before it's skipped.
  (node, text) => `${node.type.name}\u0000${node.nodeSize}\u0000${JSON.stringify(node.attrs)}\u0000${text}`,
  // Children with the same text, such as a paragraph or table row whose class changed
  (node, text) => `${node.type.name}\u0000${text}`,
  // Children of the same type
  node => node.type.name
];

/**
 * Pairs up the items of two lists of keys along a longest common subsequence.
 * @param from The keys to pair from.
 * @param to The keys to pair to.
 * @returns The paired indexes in increasing order, or null when the lists differ by more than {@link MaxAlignmentEdits} edits.
 */
function alignKeys(from: readonly number[], to: readonly number[]): [number, number][] | null {
  // Most edits leave the start and end of a list alone, so pair those up before paying for the full alignment
  let start = 0;
  while (start < from.length && start < to.length && from[start] === to[start]) {
    start += 1;
  }

  let fromEnd = from.length;
  let toEnd = to.length;
  while (fromEnd > start && toEnd > start && from[fromEnd - 1] === to[toEnd - 1]) {
    fromEnd -= 1;
    toEnd -= 1;
  }

  const pairs: [number, number][] = [];
  for (let i = 0; i < start; i += 1) {
    pairs.push([i, i]);
  }

  if (fromEnd > start && toEnd > start) {
    const middlePairs = alignKeysWithMyers(from.slice(start, fromEnd), to.slice(start, toEnd));
    if (!middlePairs) {
      return null;
    }
    for (const [fromIndex, toIndex] of middlePairs) {
      pairs.push([start + fromIndex, start + toIndex]);
    }
  }

  for (let i = 0; i < from.length - fromEnd; i += 1) {
    pairs.push([fromEnd + i, toEnd + i]);
  }

  return pairs;
}

/**
 * Finds a longest common subsequence of two lists of keys with Myers' diff algorithm.
 * Its time is proportional to the length of the lists times the number of edits between them, so it's fast when the lists
 * are mostly the same.
 * @param from The keys to pair from.
 * @param to The keys to pair to.
 * @returns The paired indexes in increasing order, or null when the lists differ by more than {@link MaxAlignmentEdits} edits.
 */
function alignKeysWithMyers(from: readonly number[], to: readonly number[]): [number, number][] | null {
  const maxEdits = Math.min(from.length + to.length, MaxAlignmentEdits);
  // furthest[offset + k] is the furthest index in `from` reached on diagonal k (the `from` index minus the `to` index)
  const offset = maxEdits + 1;
  const furthest = new Int32Array(2 * maxEdits + 3);
  // A copy of the reachable diagonals (-edits to edits) after each number of edits, to trace the pairs back from the end
  const trace: Int32Array[] = [];

  for (let edits = 0; edits <= maxEdits; edits += 1) {
    for (let k = -edits; k <= edits; k += 2) {
      // Reach diagonal k by inserting a `to` item from diagonal k + 1 or deleting a `from` item from diagonal k - 1, whichever got further
      let fromIndex = k === -edits || (k !== edits && furthest[offset + k - 1] < furthest[offset + k + 1]) ? furthest[offset + k + 1] : furthest[offset + k - 1] + 1;
      let toIndex = fromIndex - k;
      while (fromIndex < from.length && toIndex < to.length && from[fromIndex] === to[toIndex]) {
        fromIndex += 1;
        toIndex += 1;
      }
      furthest[offset + k] = fromIndex;

      if (fromIndex >= from.length && toIndex >= to.length) {
        trace.push(furthest.slice(offset - edits, offset + edits + 1));
        return traceMyersPairs(from.length, to.length, trace);
      }
    }
    trace.push(furthest.slice(offset - edits, offset + edits + 1));
  }

  return null;
}

/**
 * Reads the pairs of matching items off the trace of {@link alignKeysWithMyers}, from the end of the lists back to the start.
 * @param fromLength The length of the list to pair from.
 * @param toLength The length of the list to pair to.
 * @param trace The reachable diagonals after each number of edits. `trace[edits][k + edits]` is the furthest `from` index on diagonal k.
 * @returns The paired indexes in increasing order.
 */
function traceMyersPairs(fromLength: number, toLength: number, trace: Int32Array[]): [number, number][] {
  const pairs: [number, number][] = [];
  let fromIndex = fromLength;
  let toIndex = toLength;

  for (let edits = trace.length - 1; edits > 0; edits -= 1) {
    const previous = trace[edits - 1];
    const k = fromIndex - toIndex;
    // Make the same choice the forward pass made to reach diagonal k
    const inserted = k === -edits || (k !== edits && previous[k - 1 + edits - 1] < previous[k + 1 + edits - 1]);
    const previousK = inserted ? k + 1 : k - 1;
    const previousFromIndex = previous[previousK + edits - 1];
    const previousToIndex = previousFromIndex - previousK;
    // The matching items followed after the insert or delete
    const matchStartFromIndex = inserted ? previousFromIndex : previousFromIndex + 1;
    while (fromIndex > matchStartFromIndex) {
      fromIndex -= 1;
      toIndex -= 1;
      pairs.push([fromIndex, toIndex]);
    }
    fromIndex = previousFromIndex;
    toIndex = previousToIndex;
  }

  // The matching items at the start, before any edit
  while (fromIndex > 0 && toIndex > 0) {
    fromIndex -= 1;
    toIndex -= 1;
    pairs.push([fromIndex, toIndex]);
  }

  return pairs.reverse();
}

/**
 * Finds the edits that change the children of one node into the children of another.
 *
 * The children are paired along a longest common subsequence of {@link ChildAlignmentKeys}, strictest key first. Each run of
 * children left between two pairs is paired by the next key. Children left over after the last key are paired in order, and the
 * extras on the longer side are deleted or inserted. A pair only gets an edit when its children aren't equal.
 *
 * Pairing by text before type keeps rows matched up when a commit changes every row (such as a restyle) and also adds or removes rows.
 * @param fromNode The node to change the children of.
 * @param toNode The node with the children to change them to.
 * @returns The edits in document order.
 */
function alignChildren(fromNode: ProseMirrorNode, toNode: ProseMirrorNode): ChildEdit[] {
  const fromChildren: ProseMirrorNode[] = [];
  fromNode.forEach(child => fromChildren.push(child));
  const toChildren: ProseMirrorNode[] = [];
  toNode.forEach(child => toChildren.push(child));

  // Children are compared by key ids, so each key string is only compared once
  const keyIds = new Map<string, number>();
  // A child's text is used by more than one key, so it's only read once
  const texts = new Map<ProseMirrorNode, string>();

  /** Gets the ids of the {@link ChildAlignmentKeys} at `level` for the children in [start, end). */
  const getKeys = (children: ProseMirrorNode[], start: number, end: number, level: number): number[] => {
    const keys: number[] = [];
    for (let i = start; i < end; i += 1) {
      const child = children[i];
      let text = texts.get(child);
      if (text === undefined) {
        text = child.textContent;
        texts.set(child, text);
      }
      const key = ChildAlignmentKeys[level](child, text);
      let id = keyIds.get(key);
      if (id === undefined) {
        id = keyIds.size;
        keyIds.set(key, id);
      }
      keys.push(id);
    }
    return keys;
  };

  const edits: ChildEdit[] = [];

  /**
   * Adds the edits that change the children in [fromStart, fromEnd) into the children in [toStart, toEnd), aligning them by the
   * {@link ChildAlignmentKeys} at `level`. Past the last key, the children are paired in order.
   */
  const alignRun = (fromStart: number, fromEnd: number, toStart: number, toEnd: number, level: number) => {
    let pairs: [number, number][];

    if (level < ChildAlignmentKeys.length) {
      pairs = alignKeys(getKeys(fromChildren, fromStart, fromEnd, level), getKeys(toChildren, toStart, toEnd, level));
      // Too many edits to align by this key, so align the whole run by the next one
      if (!pairs) {
        alignRun(fromStart, fromEnd, toStart, toEnd, level + 1);
        return;
      }
    } else {
      // No keys are left, so pair the children in order and delete or insert the extras at the end
      pairs = [];
      for (let i = 0; i < Math.min(fromEnd - fromStart, toEnd - toStart); i += 1) {
        pairs.push([i, i]);
      }
    }

    let fromIndex = fromStart;
    let toIndex = toStart;
    // The final pair marks the end of the run so the children after the last real pair are aligned too
    for (const [pairFrom, pairTo] of [...pairs.map(([a, b]): [number, number] => [fromStart + a, toStart + b]), [fromEnd, toEnd]]) {
      if (pairFrom > fromIndex && pairTo > toIndex && level < ChildAlignmentKeys.length) {
        // Both sides have unpaired children here, so try to pair them by the next key
        alignRun(fromIndex, pairFrom, toIndex, pairTo, level + 1);
      } else {
        for (; fromIndex < pairFrom; fromIndex += 1) {
          edits.push({ type: 'delete', fromIndex });
        }
        for (; toIndex < pairTo; toIndex += 1) {
          edits.push({ type: 'insert', fromIndex, toIndex });
        }
      }

      if (pairFrom < fromEnd) {
        // Equal children need no edit
        if (fromChildren[pairFrom] !== toChildren[pairTo] && !fromChildren[pairFrom].eq(toChildren[pairTo])) {
          edits.push({ type: 'patch', fromIndex: pairFrom, toIndex: pairTo });
        }
        fromIndex = pairFrom + 1;
        toIndex = pairTo + 1;
      }
    }
  };

  alignRun(0, fromChildren.length, 0, toChildren.length, 0);

  return edits;
}

/**
 * Checks whether an error thrown while changing part of a document means that part should be changed another way.
 * These are the errors ProseMirror throws for a step the document rejects and the error thrown when the patch operations can't be applied.
 * @param ex The error that was thrown.
 * @returns True if a different way of changing the node should be tried, false if the error should be rethrown.
 */
function isRecoverablePatchError(ex: unknown): boolean {
  const error = ex as Error;
  // A stack overflow has to reach recreateTransformWithJSONPatch so it can retry with the fast-json-patch patcher
  if (error?.message === StackSizeLimitError) {
    return false;
  }

  return error?.name === 'Operation Cannot Be Applied' || error?.name === 'TransformError' || error instanceof RangeError || error instanceof ReplaceError;
}

/**
 * Changes a node on a separate transform and adds the steps to the transform when the node is changed into the expected node.
 * The transform is left untouched when the change fails so another way of changing the node can be tried.
 * @param transform The transform to add the steps to.
 * @param path The child index path of the node to change.
 * @param toNode The node to change it to.
 * @param change Adds the steps that change the node to the given transform.
 * @returns True if the node was changed, false otherwise.
 */
function tryChangeNode(transform: Transform, path: number[], toNode: ProseMirrorNode, change: (transform: Transform) => void): boolean {
  const attempt = new Transform(transform.doc);

  try {
    change(attempt);

    // Steps inside a node can have effects outside of it (eg a replace that doesn't fit being placed elsewhere), so check the result.
    // Those effects can also change the parent's child count so the path no longer resolves, which throws a RangeError. Checking
    // inside the try fails just this attempt, rather than letting the error fail the parent's attempt along with its other children.
    if (!resolveIndexPath(attempt.doc, path).node.eq(toNode)) {
      return false;
    }
  } catch (ex) {
    if (isRecoverablePatchError(ex)) {
      return false;
    }
    throw ex;
  }

  appendTransform(transform, attempt);

  return true;
}

/**
 * Appends the steps of a transform that started from another transform's current doc onto that transform.
 * This records the already applied steps and docs the same way `Transform.step` does, without applying every step again.
 * Applying them again would repeat the work of each nested attempt once per level of the document.
 * @param transform The transform to append the steps to.
 * @param attempt The transform with the steps to append. It must have started from `transform.doc`.
 */
function appendTransform(transform: Transform, attempt: Transform) {
  for (let i = 0; i < attempt.steps.length; i += 1) {
    // docs[i] is the doc before steps[i]
    transform.docs.push(attempt.docs[i]);
    transform.steps.push(attempt.steps[i]);
  }
  transform.mapping.appendMapping(attempt.mapping);
  transform.doc = attempt.doc;
}

/**
 * Checks whether a node can be changed into another by changing its children one at a time.
 * The node has to keep its type. Textblocks are diffed as a whole because their inline children merge and split as they change.
 * @param fromNode The node to change from.
 * @param toNode The node to change to.
 * @param path The child index path of the node.
 * @returns True if the node's children can be changed one at a time.
 */
function canChangeChildren(fromNode: ProseMirrorNode, toNode: ProseMirrorNode, path: number[]): boolean {
  // The root can't have its markup changed by a step
  return fromNode.type === toNode.type && !fromNode.type.inlineContent && !fromNode.isLeaf && (path.length > 0 || fromNode.sameMarkup(toNode));
}

/**
 * Adds steps to the transform that change the children of the node at a path into the children of another node.
 * The node's markup is changed first, then each child is deleted, inserted, or changed with {@link patchNode}.
 * @param transform The transform to add the steps to.
 * @param path The child index path of the node.
 * @param toNode The node with the markup and children to change to.
 * @param jsonPatcher The JSON patcher to use for changing the children.
 */
function changeChildren(transform: Transform, path: number[], toNode: ProseMirrorNode, jsonPatcher: JSONPatcher) {
  const { node: fromNode, pos } = resolveIndexPath(transform.doc, path);

  if (!fromNode.sameMarkup(toNode)) {
    transform.setNodeMarkup(pos, toNode.type, toNode.attrs, toNode.marks);
  }

  const edits = alignChildren(fromNode, toNode);

  // Apply the edits from last to first so the indexes and positions of the children before each edit are unchanged
  for (let i = edits.length - 1; i >= 0; i -= 1) {
    const edit = edits[i];
    if (edit.type === 'patch') {
      patchNode(transform, [...path, edit.fromIndex], toNode.child(edit.toIndex), jsonPatcher);
    } else if (edit.type === 'delete') {
      const { node: child, pos: childPos } = resolveIndexPath(transform.doc, [...path, edit.fromIndex]);
      transform.delete(childPos, childPos + child.nodeSize);
    } else {
      const parent = resolveIndexPath(transform.doc, path);
      transform.insert(getChildPos(parent.node, parent.pos, edit.fromIndex), toNode.child(edit.toIndex));
    }
  }
}

/**
 * Adds steps to the transform that change the node at a path into another node.
 *
 * Diffing a whole large document is slow: each patch operation is followed by a diff of the whole document, so the cost grows with
 * the number of changes times the size of the document. So instead of diffing the whole document this narrows the diff to the parts
 * that changed. Nodes that keep their type have their children aligned and changed one at a time, down to the textblocks and other
 * nodes that changed as a whole, which are then diffed on their own. Those diffs are small so their cost no longer depends on the
 * size of the document.
 *
 * If a node can't be changed that way it is diffed as a whole, and if that fails it is replaced. The root is always changed.
 * @param transform The transform to add the steps to.
 * @param path The child index path of the node to change.
 * @param toNode The node to change it to.
 * @param jsonPatcher The JSON patcher to use for diffing the nodes.
 * @throws When the root can't be changed. When the root's diff can't be applied the error is named 'Operation Cannot Be Applied'.
 */
function patchNode(transform: Transform, path: number[], toNode: ProseMirrorNode, jsonPatcher: JSONPatcher) {
  const { node: fromNode, pos } = resolveIndexPath(transform.doc, path);
  if (fromNode.eq(toNode)) {
    return;
  }

  if (canChangeChildren(fromNode, toNode, path) && tryChangeNode(transform, path, toNode, attempt => changeChildren(attempt, path, toNode, jsonPatcher))) {
    return;
  }

  // The root is diffed as a whole, which is the original algorithm. Its errors are handled by the caller.
  if (path.length === 0) {
    applyJSONPatch(transform, path, toNode, jsonPatcher);
    return;
  }

  if (tryChangeNode(transform, path, toNode, attempt => applyJSONPatch(attempt, path, toNode, jsonPatcher))) {
    return;
  }

  // Replace just this node rather than the whole document. If the node can't be replaced here the error falls back to the parent.
  transform.replaceWith(pos, pos + fromNode.nodeSize, toNode);
}

/**
 * Creates a transform to get from one document to another using the given JSON patcher.
 * Only the parts of the document that changed are diffed (see {@link patchNode}).
 * If the root's diff can't be applied, or the steps don't produce the document to change to, it falls back to a full document replace.
 * @param fromDoc The document to change from.
 * @param toDoc The document to change to.
 * @param jsonPatcher The JSON patcher to use for applying the patch.
 * @returns The transform that can be applied to the fromDoc to get to the toDoc.
 */
function recreateTransformWithPatcher(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode, jsonPatcher: JSONPatcher): Transform {
  let transform: Transform;

  try {
    transform = new Transform(fromDoc);
    patchNode(transform, [], toDoc, jsonPatcher);

    // Guarantee the steps produce the document to change to
    if (!transform.doc.eq(toDoc)) {
      throw new CustomError('Operation Cannot Be Applied', 'The patched doc does not match the doc to change to');
    }
  } catch (ex) {
    if (ex.name === 'Operation Cannot Be Applied') {
      transform = applyFullReplace(fromDoc, toDoc);
    } else {
      throw ex;
    }
  }

  return simplifyTransform(transform);
}

/**
 * Creates a ProseMirror transform to get from one document to another.
 * Only the parts of the document that changed are diffed, using the RFC6902 patcher by default.
 * If that fails due to a stack size limit error, then it will fall back to the fast-json-patch patcher.
 * If all patching attempts fail, then it will fall back to a full document replace.
 * The RFC6902 patcher generates cleaner patches that work better with ProseMirror and better match human expectations.
 * But RFC6902 can hit the stack size limit on a node with deeply nested or very large content, so we fall back to fast-json-patch.
 * @param fromDoc The document to change from.
 * @param toDoc The document to change to.
 * @param jsonPatcherType The JSON patcher to diff the changed parts with.
 * @returns The transform that can be applied to the fromDoc to get to the toDoc.
 */
export function recreateTransformWithJSONPatch(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode, jsonPatcherType: JSONPatcherType = 'rfc6902'): Transform {
  let transform: Transform;
  const jsonPatcher = jsonPatcherType === 'rfc6902' ? rfc6902JSONPatcher : fastJSONPatchJSONPatcher;

  try {
    transform = recreateTransformWithPatcher(fromDoc, toDoc, jsonPatcher);
  } catch (ex) {
    // If the stack size limit is exceeded, fall back to fast-json-patch. As far as we know this only happens with the RFC6902 patcher.
    if (ex.message === StackSizeLimitError && jsonPatcherType !== 'fast-json-patch') {
      transform = recreateTransformWithPatcher(fromDoc, toDoc, fastJSONPatchJSONPatcher);
    } else {
      throw ex;
    }
  }

  return transform;
}
